/**
 * Public customer intake for the personalised pet photo book.
 *
 * URL:  /create-story                              -> fresh session
 * URL:  /create-story/:submissionId/:token         -> resume existing
 *
 * Talks to the token-gated public API under /api/studio/public/submissions.
 * On first submit we also stash {id, token, email} in localStorage so a
 * customer bouncing between phone and desktop on the same device auto-
 * resumes without a copy-paste.
 *
 * Mobile-first design:
 *  - Big touch targets (min 44px)
 *  - Single column with sticky footer nav
 *  - @dnd-kit sortable grid for photo reorder (works on touch)
 *  - Uploads happen one file at a time so a flaky phone connection can
 *    resume mid-batch without losing what already went through.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Loader2, Upload, Trash2, GripVertical, Check, ChevronLeft, ChevronRight, Info, PawPrint, Heart, ImagePlus, BookOpen, Sparkles, AlertCircle, RefreshCw } from "lucide-react";
import UploadBanner from "../components/UploadBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "snuggleprints_intake_session";

const STEPS = [
  { id: "pet", label: "About", icon: PawPrint },
  { id: "bond", label: "Your bond", icon: Heart },
  { id: "photos", label: "Photos", icon: ImagePlus },
  { id: "favorites", label: "Favorite toys", icon: Sparkles },
  { id: "covers", label: "Covers", icon: BookOpen },
  { id: "review", label: "Review", icon: Check },
];

const TARGET_PHOTOS = 16;
const TARGET_FAVORITES_MIN = 5;
const TARGET_FAVORITES_MAX = 7;

const btnPrimary = "inline-flex items-center justify-center gap-2 bg-[#E6C982] text-[#0F1115] rounded-lg px-5 py-3 text-sm font-semibold min-h-[44px] disabled:opacity-60 transition hover:brightness-105";
const btnGhost = "inline-flex items-center justify-center gap-2 border border-[#1E232D] text-white/70 hover:text-white hover:border-white/30 rounded-lg px-4 py-3 text-sm min-h-[44px] transition";
const inputCls = "w-full bg-[#0F1115] border border-[#1E232D] rounded-lg px-3 py-3 text-sm text-white placeholder-white/30 focus:border-[#E6C982] focus:outline-none transition min-h-[44px]";

export default function CreateStoryPage() {
  const { submissionId: routeSid, token: routeTok } = useParams();
  const navigate = useNavigate();
  // Session state — either resumed from URL, from localStorage, or fresh.
  const [session, setSession] = useState(() => {
    if (routeSid && routeTok) return { id: routeSid, token: routeTok };
    try {
      const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (cached?.id && cached?.token) return cached;
    } catch { /* ignore corrupt cache */ }
    return null;
  });
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const persistSession = (s) => {
    setSession(s);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* quota */ }
  };
  const clearSession = () => {
    setSession(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  // Fetch submission whenever we have a session (from URL / storage).
  const refetch = async () => {
    if (!session?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/studio/public/submissions/${session.id}?token=${session.token}`
      );
      setSubmission(r.data.submission);
      if (r.data.submission.submitted_at) setSubmitted(true);
    } catch (e) {
      // Bad/expired token — reset so the customer can start over.
      if (e?.response?.status === 403 || e?.response?.status === 404) {
        clearSession();
        setSubmission(null);
        toast.error("Session expired — please start again");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refetch(); }, [session?.id]);

  // ---- Landing (no session yet) ----
  if (!session || !submission) {
    return (
      <IntakeLanding
        loading={loading}
        onStart={async (data) => {
          setLoading(true);
          try {
            const r = await axios.post(`${API}/studio/public/submissions`, data);
            persistSession({ id: r.data.submission_id, token: r.data.token, email: data.submission_email });
            setSubmission(r.data.submission);
            // Update the URL so a bookmark works cross-device.
            navigate(`/create-story/${r.data.submission_id}/${r.data.token}`, { replace: true });
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start submission");
          } finally {
            setLoading(false);
          }
        }}
        onResume={async (email) => {
          try {
            const r = await axios.post(`${API}/studio/public/submissions/resume-by-email`, { email });
            const { submission_id, token, submission: sub } = r.data;
            persistSession({ id: submission_id, token, email });
            setSubmission(sub);
            if (sub?.submitted_at) setSubmitted(true);
            navigate(`/create-story/${submission_id}/${token}`, { replace: true });
            toast.success("Welcome back — picking up where you left off");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not find that story");
          }
        }}
      />
    );
  }

  if (submitted) return <SubmittedThanks email={submission.submission_email} />;

  // ---- Wizard ----
  const step = STEPS[stepIdx].id;
  const stepProps = { session, submission, refetch, setSubmitted, navigate };
  return (
    <div className="min-h-screen bg-[#0B0D12] text-white">
      <IntakeHeader submission={submission} onExit={clearSession} />
      <ProgressBar stepIdx={stepIdx} />
      <main className="max-w-2xl mx-auto px-4 pb-40 pt-4">
        {step === "pet" && <StepPet {...stepProps} />}
        {step === "bond" && <StepBond {...stepProps} />}
        {step === "photos" && <StepPhotos {...stepProps} />}
        {step === "favorites" && <StepFavorites {...stepProps} />}
        {step === "covers" && <StepCovers {...stepProps} />}
        {step === "review" && <StepReview {...stepProps} />}
      </main>
      <StickyNav
        stepIdx={stepIdx}
        onBack={() => setStepIdx((i) => Math.max(0, i - 1))}
        onNext={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
      />
    </div>
  );
}

/* ---------------------------------- Landing ---------------------------------- */
function IntakeLanding({ loading, onStart, onResume }) {
  const [mode, setMode] = useState("start"); // start | resume
  const [email, setEmail] = useState("");
  const [petName, setPetName] = useState("");
  const [resumeEmail, setResumeEmail] = useState("");
  const [resumeBusy, setResumeBusy] = useState(false);
  const canStart = email.includes("@") && email.includes(".") && petName.trim().length > 0;
  const canResume = resumeEmail.includes("@") && resumeEmail.includes(".");

  const doResume = async () => {
    setResumeBusy(true);
    try {
      await onResume(resumeEmail.trim().toLowerCase());
    } finally {
      setResumeBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white">
      <div className="max-w-lg mx-auto px-5 pt-14 pb-24">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E6C982]/80 mb-2">The Snuggle Prints</div>
        <h1 className="text-3xl sm:text-4xl font-serif leading-tight mb-4">Your pet&rsquo;s personalised storybook</h1>
        <p className="text-white/60 text-sm leading-relaxed mb-6">
          Answer a few questions, upload your favourite photos of your pet, and we&rsquo;ll turn them into a hand-painted illustrated keepsake book — a page for every memory, plus a closing dedication written just for them.
        </p>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2 mb-6 p-1 bg-[#0F1115] border border-[#1E232D] rounded-lg">
          <button
            onClick={() => setMode("start")}
            className={`text-sm py-2 rounded-md transition ${
              mode === "start" ? "bg-[#E6C982] text-[#0F1115] font-semibold" : "text-white/60 hover:text-white"
            }`}
            data-testid="intake-mode-start"
          >
            Start a new story
          </button>
          <button
            onClick={() => setMode("resume")}
            className={`text-sm py-2 rounded-md transition ${
              mode === "resume" ? "bg-[#E6C982] text-[#0F1115] font-semibold" : "text-white/60 hover:text-white"
            }`}
            data-testid="intake-mode-resume"
          >
            Continue where I left off
          </button>
        </div>

        {mode === "start" ? (
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Your email · so we can save your progress</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="[email protected]"
                className={inputCls}
                autoComplete="email"
                inputMode="email"
                data-testid="intake-landing-email"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Your pet&rsquo;s name</label>
              <input
                type="text"
                value={petName}
                onChange={(e) => setPetName(e.target.value)}
                placeholder="Bruno"
                className={inputCls}
                data-testid="intake-landing-pet-name"
              />
            </div>
            <button
              disabled={!canStart || loading}
              onClick={() => onStart({ submission_email: email.trim().toLowerCase(), pet_name: petName.trim() })}
              className={btnPrimary + " w-full mt-2"}
              data-testid="intake-landing-start"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
              {loading ? "Starting…" : `Start ${petName ? `${petName.trim()}'s ` : ""}book`}
            </button>
            <p className="text-[11px] text-white/40 leading-relaxed pt-2">
              We&rsquo;ll save your progress automatically. You can come back later on any device — just tap &ldquo;Continue where I left off&rdquo; and enter your email.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">The email you started with</label>
              <input
                type="email"
                value={resumeEmail}
                onChange={(e) => setResumeEmail(e.target.value)}
                placeholder="[email protected]"
                className={inputCls}
                autoComplete="email"
                inputMode="email"
                data-testid="intake-resume-email"
                onKeyDown={(e) => e.key === "Enter" && canResume && !resumeBusy && doResume()}
              />
            </div>
            <button
              disabled={!canResume || resumeBusy}
              onClick={doResume}
              className={btnPrimary + " w-full mt-2"}
              data-testid="intake-resume-submit"
            >
              {resumeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
              {resumeBusy ? "Looking…" : "Take me back to my story"}
            </button>
            <p className="text-[11px] text-white/40 leading-relaxed pt-2">
              We&rsquo;ll load whatever you&rsquo;ve already saved so you can carry on from any phone or laptop.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------- Chrome ---------------------------------- */
function IntakeHeader({ submission, onExit }) {
  return (
    <header className="sticky top-0 z-20 bg-[#0B0D12]/90 backdrop-blur border-b border-[#1E232D]">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E6C982]/70">Snuggle Prints</div>
          <div className="text-sm font-medium truncate max-w-[60vw]">{submission.pet_name || "Your pet"}&rsquo;s book</div>
        </div>
        <button onClick={onExit} className="text-[11px] text-white/40 hover:text-white" data-testid="intake-exit">
          Exit
        </button>
      </div>
    </header>
  );
}

function ProgressBar({ stepIdx }) {
  const pct = ((stepIdx + 1) / STEPS.length) * 100;
  return (
    <div className="max-w-2xl mx-auto px-4 pt-3">
      <div className="h-1.5 bg-[#1E232D] rounded-full overflow-hidden">
        <div className="h-full bg-[#E6C982] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-wider text-white/40">
        <span>Step {stepIdx + 1} of {STEPS.length}</span>
        <span className="text-[#E6C982]/80">{STEPS[stepIdx].label}</span>
      </div>
    </div>
  );
}

function StickyNav({ stepIdx, onBack, onNext }) {
  const isLast = stepIdx === STEPS.length - 1;
  return (
    <div className="fixed bottom-0 inset-x-0 bg-[#0B0D12]/95 backdrop-blur border-t border-[#1E232D] z-10">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          disabled={stepIdx === 0}
          className={btnGhost + (isLast ? " w-full" : " flex-1") + " disabled:opacity-30"}
          data-testid="intake-nav-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        {!isLast && (
          <button onClick={onNext} className={btnPrimary + " flex-[2]"} data-testid="intake-nav-next">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function InfoBanner({ children, testId }) {
  return (
    <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-500/30 text-blue-100 rounded-lg p-3 text-xs" data-testid={testId}>
      <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-300" />
      <div>{children}</div>
    </div>
  );
}

/* ---------------------------------- Steps ---------------------------------- */
function StepHeader({ title, subtitle }) {
  return (
    <div className="mb-5">
      <h2 className="text-2xl font-serif mb-1">{title}</h2>
      {subtitle && <p className="text-sm text-white/50 leading-relaxed">{subtitle}</p>}
    </div>
  );
}

async function patch(session, body) {
  return axios.patch(
    `${API}/studio/public/submissions/${session.id}?token=${session.token}`,
    body
  );
}

function StepPet({ session, submission, refetch }) {
  const [draft, setDraft] = useState({
    pet_name: submission.pet_name || "",
    pet_species: submission.pet_species || "",
    pet_gender: submission.pet_gender || "",
    pet_breed: submission.pet_breed || "",
  });
  useEffect(() => setDraft({
    pet_name: submission.pet_name || "",
    pet_species: submission.pet_species || "",
    pet_gender: submission.pet_gender || "",
    pet_breed: submission.pet_breed || "",
  }), [submission.id]);

  const save = async (patchBody) => {
    try { await patch(session, patchBody); refetch(); } catch { toast.error("Save failed"); }
  };
  return (
    <div>
      <StepHeader title="About your pet" subtitle="A few quick details so we know who this book is for." />
      <div className="space-y-4">
        <Field label="Pet name">
          <input
            className={inputCls}
            value={draft.pet_name}
            onChange={(e) => setDraft((d) => ({ ...d, pet_name: e.target.value }))}
            onBlur={() => draft.pet_name !== submission.pet_name && save({ pet_name: draft.pet_name })}
            placeholder="Bruno"
            data-testid="intake-pet-name"
          />
        </Field>
        <Field label="Species" hint="Type dog, cat, rabbit, or whatever fits your pet.">
          <input
            className={inputCls}
            value={draft.pet_species}
            onChange={(e) => setDraft((d) => ({ ...d, pet_species: e.target.value }))}
            onBlur={() => draft.pet_species !== submission.pet_species && save({ pet_species: draft.pet_species })}
            placeholder="dog"
            data-testid="intake-pet-species"
          />
        </Field>
        <Field label="Gender" hint="Male, female, or whatever your pet identifies as.">
          <input
            className={inputCls}
            value={draft.pet_gender}
            onChange={(e) => setDraft((d) => ({ ...d, pet_gender: e.target.value }))}
            onBlur={() => draft.pet_gender !== submission.pet_gender && save({ pet_gender: draft.pet_gender })}
            placeholder="male"
            data-testid="intake-pet-gender"
          />
        </Field>
        <Field label="Breed">
          <input
            className={inputCls}
            value={draft.pet_breed}
            onChange={(e) => setDraft((d) => ({ ...d, pet_breed: e.target.value }))}
            onBlur={() => draft.pet_breed !== submission.pet_breed && save({ pet_breed: draft.pet_breed })}
            placeholder="Golden retriever"
            data-testid="intake-pet-breed"
          />
        </Field>
      </div>
    </div>
  );
}

function StepBond({ session, submission, refetch }) {
  const [notes, setNotes] = useState(submission.pet_notes || "");
  useEffect(() => setNotes(submission.pet_notes || ""), [submission.id]);
  const wordCount = notes.trim() ? notes.trim().split(/\s+/).length : 0;
  const enough = wordCount >= 50;
  return (
    <div>
      <StepHeader
        title={`Your bond with ${submission.pet_name || "your pet"}`}
        subtitle="Tell us about your special bond — how you met, favourite memories, quirks, the everyday things that make them them. Six to eight lines or more."
      />
      <textarea
        rows={10}
        className={inputCls + " min-h-[260px] leading-relaxed"}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={async () => {
          if (notes !== submission.pet_notes) {
            try { await patch(session, { pet_notes: notes }); refetch(); }
            catch { toast.error("Save failed"); }
          }
        }}
        placeholder={`We adopted ${submission.pet_name || "him"} on a rainy Tuesday, and from the moment we opened the door…`}
        data-testid="intake-pet-notes"
      />
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className={enough ? "text-emerald-400" : "text-white/40"}>
          {wordCount} words {enough ? "· 🎉 rich enough for the AI" : "· aim for 50+ words"}
        </span>
      </div>
    </div>
  );
}

function StepPhotos({ session, submission, refetch }) {
  const photos = submission.photos || [];
  return (
    <UploadableSortableGrid
      session={session}
      items={photos}
      onChanged={refetch}
      apiSegment="photos"
      reorderPath="photos/reorder"
      title="Story photos"
      subtitle={`Pick your favourite ${TARGET_PHOTOS} photos of ${submission.pet_name || "your pet"}. Each becomes a page in the book, so add a short note about why each one is special. Long-press or drag any tile to reorder.`}
      captionPlaceholder="Why is this photo special? A memory, a moment, a feeling…"
      overLimit={TARGET_PHOTOS}
      overMessage={(n) => `You have ${n} photos. Our books fit up to ${TARGET_PHOTOS} — we&rsquo;ll use the first ${TARGET_PHOTOS} in order unless you delete some. Fewer is fine.`}
      emptyLabel="No photos yet. Tap Upload to start."
      testIdPrefix="intake-photo"
    />
  );
}

function StepFavorites({ session, submission, refetch }) {
  const items = submission.favorite_things || [];
  return (
    <UploadableSortableGrid
      session={session}
      items={items}
      onChanged={refetch}
      apiSegment="favorite-things"
      reorderPath={null}  // no reorder for favorites — order doesn't matter
      title={`${submission.pet_name || "Your pet"}'s favourite things`}
      subtitle={`Upload photos of ${TARGET_FAVORITES_MIN}–${TARGET_FAVORITES_MAX} of their favourite toys, treats, belongings — a chewed rope, a much-loved blanket, that one specific ball. We&rsquo;ll weave them into the illustrations as memory motifs.`}
      captionPlaceholder="What is it and why do they love it?"
      overLimit={TARGET_FAVORITES_MAX}
      overMessage={(n) => `You have ${n} favourites. Around ${TARGET_FAVORITES_MIN}–${TARGET_FAVORITES_MAX} is our sweet spot — a few more is fine too.`}
      emptyLabel="No favourites yet. Tap Upload to add a toy or belonging."
      testIdPrefix="intake-favorite"
    />
  );
}

function StepCovers({ session, submission, refetch }) {
  const frontPhoto = submission.cover_front_photo;
  const backPhoto = submission.cover_back_photo;
  return (
    <div>
      <StepHeader
        title="Upload your covers"
        subtitle={`Upload one photo for the front cover and one for the back — separate from your story photos. Pick the shots you love most; we'll illustrate them into a hand-painted keepsake cover.`}
      />
      <CoverUploader
        role="front"
        label="Front cover"
        session={session}
        photo={frontPhoto}
        onChanged={refetch}
        testId="intake-cover-front"
      />
      <div className="h-6" />
      <CoverUploader
        role="back"
        label="Back cover"
        session={session}
        photo={backPhoto}
        onChanged={refetch}
        testId="intake-cover-back"
      />
    </div>
  );
}

function StepReview({ session, submission, setSubmitted }) {
  const [submitting, setSubmitting] = useState(false);
  const photos = submission.photos || [];
  const favorites = submission.favorite_things || [];
  const bondWords = (submission.pet_notes || "").trim().split(/\s+/).filter(Boolean).length;
  const warnings = [];
  if (!(submission.pet_name || "").trim()) warnings.push("Add your pet's name in step 1.");
  if (bondWords < 30) warnings.push(`Your bond note is only ${bondWords} words — a few more sentences help us write a warmer story.`);
  if (photos.length === 0) warnings.push("Upload at least 1 story photo.");
  if (photos.length > TARGET_PHOTOS) warnings.push(`You have ${photos.length} story photos — we&rsquo;ll only use the first ${TARGET_PHOTOS}.`);
  if (favorites.length === 0) warnings.push("Add at least one favourite toy or thing so we can weave it into the illustrations.");
  if (!submission.cover_front_photo && !submission.cover_front_photo_id) warnings.push("Upload a front cover photo.");
  if (!submission.cover_back_photo && !submission.cover_back_photo_id) warnings.push("Upload a back cover photo.");

  const canSubmit = (submission.pet_name || "").trim() && photos.length > 0 && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/studio/public/submissions/${session.id}/submit?token=${session.token}`);
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed — try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <StepHeader
        title="Almost done — review & submit"
        subtitle="Look over what you&rsquo;ve added. When you hit Submit, our team gets everything and begins painting the book."
      />
      <div className="space-y-3 mb-6">
        <SummaryRow label="Pet name" value={submission.pet_name || <em className="text-white/30">missing</em>} />
        <SummaryRow label="Species / breed" value={[submission.pet_species, submission.pet_breed].filter(Boolean).join(" · ") || <em className="text-white/30">not set</em>} />
        <SummaryRow label="Bond note" value={`${bondWords} words`} />
        <SummaryRow label="Story photos" value={`${photos.length}${photos.length > TARGET_PHOTOS ? ` (first ${TARGET_PHOTOS} used)` : ""}`} />
        <SummaryRow label="Favourite things" value={`${favorites.length}`} />
        <SummaryRow label="Front cover" value={(submission.cover_front_photo || submission.cover_front_photo_id) ? "uploaded" : <em className="text-white/30">not uploaded</em>} />
        <SummaryRow label="Back cover" value={(submission.cover_back_photo || submission.cover_back_photo_id) ? "uploaded" : <em className="text-white/30">not uploaded</em>} />
      </div>
      {warnings.length > 0 && (
        <InfoBanner testId="intake-review-warnings">
          <div className="mb-1 font-medium text-blue-100">Before you submit:</div>
          <ul className="list-disc pl-4 space-y-1">
            {warnings.map((w, i) => <li key={i} dangerouslySetInnerHTML={{ __html: w }} />)}
          </ul>
        </InfoBanner>
      )}
      <button
        onClick={submit}
        disabled={!canSubmit}
        className={btnPrimary + " w-full mt-6"}
        data-testid="intake-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {submitting ? "Submitting…" : `Submit ${submission.pet_name || "your pet"}'s book`}
      </button>
    </div>
  );
}

function SubmittedThanks({ email }) {
  return (
    <div className="min-h-screen bg-[#0B0D12] text-white flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-[#E6C982]/15 text-[#E6C982] mx-auto flex items-center justify-center mb-6">
          <Check className="w-8 h-8" />
        </div>
        <h1 className="text-3xl font-serif mb-3">Thank you!</h1>
        <p className="text-white/60 text-sm leading-relaxed">
          We&rsquo;ve got everything we need to start painting. Our team will reach out at <span className="text-white">{email}</span> within 24 hours with a preview of your book.
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------- Bits ---------------------------------- */
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-white/40 mt-1">{hint}</p>}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between border-b border-[#1E232D] py-2">
      <span className="text-[11px] uppercase tracking-wider text-white/40">{label}</span>
      <span className="text-sm text-white/90">{value}</span>
    </div>
  );
}

function CoverUploader({ role, label, session, photo, onChanged, testId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [uploadState, setUploadState] = useState(null); // null | "uploading" | "retrying" | "failed"

  const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setUploadState("uploading");
    const url = `${API}/studio/public/submissions/${session.id}/covers/${role}?token=${session.token}`;
    const maxRetries = 3;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(url, fd, { timeout: 60000 });
        setUploadState(null);
        setBusy(false);
        onChanged();
        return;
      } catch (e) {
        if (attempt < maxRetries) {
          setUploadState("retrying");
          await _sleep(1000 * Math.pow(2, attempt)); // 1 s, 2 s, 4 s
          continue;
        }
        setUploadState("failed");
        setBusy(false);
        toast.error(e?.response?.data?.detail || `Cover upload failed after ${maxRetries + 1} attempts`);
        return;
      }
    }
  };

  const remove = async () => {
    if (!confirm(`Remove the ${label.toLowerCase()} photo?`)) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/studio/public/submissions/${session.id}/covers/${role}?token=${session.token}`
      );
      onChanged();
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-white/40 mb-2">
        {label} {photo ? <span className="text-[#E6C982]">· uploaded</span> : <span className="text-white/30">· tap to upload</span>}
      </div>
      {photo ? (
        <div className="relative rounded-xl overflow-hidden border border-[#1E232D] bg-[#0F1115]">
          <img src={photo.url} alt={`${label} preview`} className="w-full aspect-[4/5] object-cover" />
          <div className="absolute bottom-0 inset-x-0 p-3 flex gap-2 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className={btnGhost + " flex-1 bg-black/40 backdrop-blur"}
              data-testid={`${testId}-replace`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Replace
            </button>
            <button
              onClick={remove}
              disabled={busy}
              className={btnGhost + " bg-black/40 backdrop-blur"}
              data-testid={`${testId}-remove`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="w-full aspect-[4/5] rounded-xl border-2 border-dashed border-[#1E232D] hover:border-[#E6C982]/60 bg-[#0F1115] flex flex-col items-center justify-center gap-3 text-white/50 hover:text-white transition min-h-[220px]"
          data-testid={`${testId}-upload`}
        >
          {busy ? (
            <Loader2 className="w-6 h-6 animate-spin text-[#E6C982]" />
          ) : (
            <>
              <ImagePlus className="w-8 h-8 text-[#E6C982]" />
              <span className="text-sm">Upload {label.toLowerCase()} photo</span>
              <span className="text-[11px] text-white/30">JPG, PNG, WEBP · up to 12 MB</span>
            </>
          )}
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (fileRef.current) fileRef.current.value = "";
          upload(f);
        }}
        data-testid={`${testId}-input`}
      />
      {/* Inline retry/fail indicator */}
      {uploadState === "retrying" && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-white/50">
          <Loader2 className="w-3 h-3 animate-spin" />
          Retrying upload…
        </div>
      )}
      {uploadState === "failed" && (
        <div className="mt-2 flex items-center gap-2 text-xs text-orange-400">
          <AlertCircle className="w-3 h-3 shrink-0" />
          Upload failed —{" "}
          <button
            onClick={() => fileRef.current?.click()}
            className="underline text-[#E6C982] hover:text-white transition"
            data-testid={`${testId}-retry`}
          >
            tap to retry
          </button>
        </div>
      )}
    </div>
  );
}

/* -------------- Uploadable sortable grid (shared by photos + favorites) -------------- */
function UploadableSortableGrid({
  session, items, onChanged,
  apiSegment, reorderPath, title, subtitle,
  captionPlaceholder, overLimit, overMessage, emptyLabel, testIdPrefix,
}) {
  const fileRef = useRef(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [order, setOrder] = useState(items.map((p) => p.id));
  useEffect(() => { setOrder(items.map((p) => p.id)); }, [items.map((p) => p.id).join(",")]);

  // True while any file is still queued or in-flight.
  const isUploading = uploadQueue.some(
    (q) => q.status === "queued" || q.status === "uploading"
  );

  // Auto-dismiss the banner 2 s after every item succeeds.
  useEffect(() => {
    if (uploadQueue.length > 0 && uploadQueue.every((q) => q.status === "done")) {
      const t = setTimeout(() => setUploadQueue([]), 2000);
      return () => clearTimeout(t);
    }
  }, [uploadQueue]);

  const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  /** Upload one file with up to 3 automatic retries (1 s → 2 s → 4 s backoff). */
  const _uploadOneWithRetry = async (itemId, file, maxRetries = 3) => {
    const url = `${API}/studio/public/submissions/${session.id}/${apiSegment}?token=${session.token}`;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(url, fd, { timeout: 60000 });
        setUploadQueue((q) =>
          q.map((x) => (x.id === itemId ? { ...x, status: "done" } : x))
        );
        return;
      } catch (e) {
        if (attempt < maxRetries) {
          await _sleep(1000 * Math.pow(2, attempt)); // 1 s, 2 s, 4 s
          continue;
        }
        const msg = e?.response?.data?.detail || `Upload failed: ${file.name}`;
        setUploadQueue((q) =>
          q.map((x) => (x.id === itemId ? { ...x, status: "failed", error: msg } : x))
        );
        throw e;
      }
    }
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const newItems = list.map((f) => ({
      id: crypto.randomUUID(),
      name: f.name,
      status: "queued",
      file: f,
    }));
    setUploadQueue((q) => [...q.filter((x) => x.status !== "done"), ...newItems]);
    for (const item of newItems) {
      setUploadQueue((q) =>
        q.map((x) => (x.id === item.id ? { ...x, status: "uploading" } : x))
      );
      try {
        await _uploadOneWithRetry(item.id, item.file);
      } catch { /* status already set to "failed" */ }
    }
    onChanged();
  };

  const retryFailed = async () => {
    const failed = uploadQueue.filter((q) => q.status === "failed");
    setUploadQueue((q) =>
      q.map((x) =>
        x.status === "failed" ? { ...x, status: "queued", error: undefined } : x
      )
    );
    for (const item of failed) {
      setUploadQueue((q) =>
        q.map((x) => (x.id === item.id ? { ...x, status: "uploading" } : x))
      );
      try {
        await _uploadOneWithRetry(item.id, item.file);
      } catch { /* marked failed again */ }
    }
    onChanged();
  };

  const dismissBanner = () => setUploadQueue([]);
  const remove = async (id) => {
    if (!confirm("Remove this photo?")) return;
    try {
      await axios.delete(
        `${API}/studio/public/submissions/${session.id}/${apiSegment}/${id}?token=${session.token}`
      );
      onChanged();
    } catch { toast.error("Delete failed"); }
  };
  const updateCaption = async (id, caption, { silent = false } = {}) => {
    try {
      await axios.patch(
        `${API}/studio/public/submissions/${session.id}/${apiSegment}/${id}?token=${session.token}`,
        { caption }
      );
      // No refetch here — the caption is local-first; refetching would
      // interrupt fast typing on a slow connection.
    } catch (e) {
      if (!silent) toast.error("Caption save failed");
      throw e;  // let the caller keep status=pending so a later flush retries
    }
  };

  // Keepalive PATCH used on tab close — plain fetch so the browser
  // completes it even after the page dies.
  const flushCaptionBeacon = (id, caption) => {
    try {
      const url = `${API}/studio/public/submissions/${session.id}/${apiSegment}/${id}?token=${session.token}`;
      fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption }),
        keepalive: true,
      });
    } catch { /* best-effort */ }
  };

  // Drag-and-drop sensors — mouse + touch + keyboard. Long-press delay
  // on touch stops accidental drags while scrolling.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const onDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const nextOrder = arrayMove(order, oldIndex, newIndex);
    setOrder(nextOrder);
    if (!reorderPath) return; // favorites — client-only reorder for visual feedback
    try {
      await axios.post(
        `${API}/studio/public/submissions/${session.id}/${reorderPath}?token=${session.token}`,
        { photo_ids: nextOrder }
      );
      onChanged();
    } catch { toast.error("Reorder failed"); }
  };

  const byId = new Map(items.map((p) => [p.id, p]));
  const ordered = order.map((id) => byId.get(id)).filter(Boolean);

  return (
    <div>
      <StepHeader title={title} subtitle={subtitle} />
      {/* Upload progress banner */}
      {uploadQueue.length > 0 && (
        <div className="mb-4 rounded-lg overflow-hidden border border-[#1E232D]">
          <UploadBanner
            queue={uploadQueue}
            onRetry={retryFailed}
            onDismiss={dismissBanner}
          />
        </div>
      )}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => fileRef.current?.click()}
          disabled={isUploading}
          className={btnPrimary + " flex-1"}
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          {isUploading
            ? (<><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>)
            : (<><Upload className="w-4 h-4" /> Upload{items.length ? " more" : ""}</>)}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/heic"
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); if (fileRef.current) fileRef.current.value = ""; }}
          data-testid={`${testIdPrefix}-upload-input`}
        />
      </div>
      {items.length > overLimit && (
        <div className="mb-3">
          <InfoBanner testId={`${testIdPrefix}-over-warning`}>{overMessage(items.length)}</InfoBanner>
        </div>
      )}
      {ordered.length === 0 ? (
        <div className="border-2 border-dashed border-[#1E232D] rounded-xl p-10 text-center text-sm text-white/40">
          {emptyLabel}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 gap-3">
              {ordered.map((p, idx) => (
                <SortableTile
                  key={p.id}
                  item={p}
                  index={idx}
                  showOrder={Boolean(reorderPath)}
                  onDelete={() => remove(p.id)}
                  onCaption={(c, opts) => updateCaption(p.id, c, opts)}
                  onFlushBeacon={(c) => flushCaptionBeacon(p.id, c)}
                  captionPlaceholder={captionPlaceholder}
                  testIdPrefix={testIdPrefix}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function SortableTile({ item, index, showOrder, onDelete, onCaption, onFlushBeacon, captionPlaceholder, testIdPrefix }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [caption, setCaption] = useState(item.caption || "");
  const [status, setStatus] = useState("idle"); // idle | pending | saving | saved | error

  // Refs for debounced autosave + tab-close flush.
  const debounceRef = useRef(null);
  const savedTickRef = useRef(null);
  const retryRef = useRef(null);
  const retryAttemptsRef = useRef(0);
  const captionRef = useRef(caption);
  const lastSavedRef = useRef(item.caption || "");
  useEffect(() => { captionRef.current = caption; }, [caption]);

  const commit = async ({ isRetry = false } = {}) => {
    const value = captionRef.current || "";
    if (value === (lastSavedRef.current || "")) return;
    setStatus("saving");
    try {
      await onCaption(value, { silent: isRetry });
      lastSavedRef.current = value;
      retryAttemptsRef.current = 0;
      setStatus("saved");
      clearTimeout(savedTickRef.current);
      savedTickRef.current = setTimeout(() => setStatus("idle"), 1400);
    } catch {
      setStatus("error");
      const attempt = retryAttemptsRef.current + 1;
      retryAttemptsRef.current = attempt;
      if (attempt <= 4) {  // capped retries: 2s, 4s, 8s, 16s then give up
        const delay = Math.min(2000 * 2 ** (attempt - 1), 16000);
        clearTimeout(retryRef.current);
        retryRef.current = setTimeout(() => {
          if ((captionRef.current || "") !== (lastSavedRef.current || "")) {
            commit({ isRetry: true });
          }
        }, delay);
      } else {
        setStatus("failed");
      }
    }
  };

  // Sync from server, but never over the customer's in-flight typing or
  // a save that's mid-flight / errored / failed.
  useEffect(() => {
    const isFocused = document.activeElement?.dataset?.testid === `${testIdPrefix}-caption-${item.id}`;
    const hasPending = status === "pending" || status === "saving" || status === "error" || status === "failed";
    if (isFocused || hasPending) return;
    setCaption(item.caption || "");
    lastSavedRef.current = item.caption || "";
  }, [item.id, item.caption]);

  // Flush unsaved edits when the tab is hidden / closed. Uses a keepalive
  // fetch (not axios) so the browser is guaranteed to complete it — an
  // async axios PATCH is killed the moment the tab dies, which is likely
  // what lost captions on the customer bug we're fixing.
  useEffect(() => {
    const flushOnHide = () => {
      const value = captionRef.current || "";
      if (value === (lastSavedRef.current || "")) return;
      clearTimeout(debounceRef.current);
      clearTimeout(retryRef.current);
      onFlushBeacon(value);
      lastSavedRef.current = value;
      setStatus("saved");
    };
    const onVis = () => { if (document.visibilityState === "hidden") flushOnHide(); };
    window.addEventListener("beforeunload", flushOnHide);
    window.addEventListener("pagehide", flushOnHide);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("beforeunload", flushOnHide);
      window.removeEventListener("pagehide", flushOnHide);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Debounced autosave — saves 800 ms after the customer stops typing so
  // nothing is ever left waiting for a blur that may never fire.
  const onCaptionChange = (next) => {
    setCaption(next);
    setStatus("pending");
    retryAttemptsRef.current = 0;
    clearTimeout(debounceRef.current);
    clearTimeout(retryRef.current);
    debounceRef.current = setTimeout(commit, 800);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    touchAction: "manipulation",
  };
  return (
    <div ref={setNodeRef} style={style} className="bg-[#0F1115] border border-[#1E232D] rounded-xl overflow-hidden">
      <div className="relative aspect-square">
        <img src={item.url} alt="" className="w-full h-full object-cover" draggable={false} />
        {showOrder && (
          <div className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] rounded px-1.5 py-0.5 font-mono">
            {index + 1}
          </div>
        )}
        <button
          {...attributes}
          {...listeners}
          className="absolute top-1.5 right-1.5 bg-black/70 text-white p-1.5 rounded touch-none active:cursor-grabbing cursor-grab"
          title="Drag to reorder"
          data-testid={`${testIdPrefix}-drag-${item.id}`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          className="absolute bottom-1.5 right-1.5 bg-black/70 hover:bg-red-500/70 text-white p-1.5 rounded"
          data-testid={`${testIdPrefix}-delete-${item.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <textarea
        rows={3}
        className="w-full bg-[#0F1115] border-0 border-t border-[#1E232D] px-3 py-2 text-xs text-white placeholder-white/30 focus:outline-none focus:bg-[#161A22] resize-none"
        value={caption}
        onChange={(e) => onCaptionChange(e.target.value)}
        onBlur={() => { clearTimeout(debounceRef.current); commit(); }}
        placeholder={captionPlaceholder}
        data-testid={`${testIdPrefix}-caption-${item.id}`}
      />
      <div className="px-3 pb-2 h-4 text-[10px] flex items-center justify-end" data-testid={`${testIdPrefix}-status-${item.id}`}>
        {status === "pending" && <span className="text-white/40">unsaved…</span>}
        {status === "saving" && (
          <span className="text-white/50 flex items-center gap-0.5">
            <Loader2 className="w-2.5 h-2.5 animate-spin" /> saving
          </span>
        )}
        {status === "saved" && <span className="text-emerald-400">saved</span>}
        {status === "error" && <span className="text-amber-400">retrying…</span>}
        {status === "failed" && (
          <button
            onClick={() => { retryAttemptsRef.current = 0; commit(); }}
            className="text-red-400 hover:text-red-300 underline"
            data-testid={`${testIdPrefix}-retry-${item.id}`}
          >
            save failed · tap to retry
          </button>
        )}
      </div>
    </div>
  );
}
